import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function MachineLearning() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/9dfe29ad0517b78c5b1586cb181183cb.jpg"
            alt="Machine Learning Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 via-blue-800/75 to-indigo-900/85"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-center w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Enterprise Machine Learning Solutions
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
            Transform your business with intelligent automation and predictive analytics powered by cutting-edge ML algorithms
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">95%</div>
              <div className="text-blue-100">Prediction Accuracy</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">40%</div>
              <div className="text-blue-100">Cost Reduction</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">3x</div>
              <div className="text-blue-100">Faster Insights</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core ML Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Machine Learning Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our end-to-end machine learning services help enterprises harness the power of data to solve complex business problems, optimize operations, and create new opportunities for growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* ML Consulting */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20analyzing%20machine%20learning%20data%20dashboards%20and%20analytics%20on%20multiple%20screens%20in%20modern%20office%20environment%20with%20blue%20ambient%20lighting%20and%20technology%20visualization%20displays&width=400&height=300&seq=mlconsult001&orientation=landscape"
                  alt="ML Consulting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-lightbulb-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">ML Consulting</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Strategic guidance on how to leverage machine learning for your specific business challenges and opportunities.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>ML readiness assessment</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Solution architecture</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>ROI analysis</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Custom Model Development */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Advanced%20machine%20learning%20code%20and%20mathematical%20algorithms%20displayed%20on%20dark%20blue%20screens%20with%20neural%20network%20diagrams%20geometric%20patterns%20and%20data%20visualization%20charts%20showing%20model%20development%20process&width=400&height=300&seq=mlmodel001&orientation=landscape"
                  alt="Custom Model Development"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-code-box-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Model Development</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  End-to-end development of custom machine learning models tailored to your specific business requirements.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Data preparation</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Algorithm selection</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Model training & validation</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Integration Services */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Cloud%20computing%20network%20infrastructure%20diagram%20with%20connected%20devices%20servers%20and%20data%20flow%20visualization%20showing%20seamless%20system%20integration%20with%20blue%20technology%20icons%20and%20network%20topology%20on%20light%20background&width=400&height=300&seq=mlintegrate001&orientation=landscape"
                  alt="Integration Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-links-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Integration Services</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Seamless integration of machine learning models into your existing systems, applications, and workflows.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>API development</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Workflow automation</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Legacy system integration</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Industry Solutions */}
            <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Futuristic%20machine%20learning%20industry%20applications%20showing%20healthcare%20medical%20AI%20financial%20services%20manufacturing%20automation%20with%20blue%20glowing%20ML%20neural%20network%20icons%20and%20industry%20symbols%20on%20dark%20blue%20background&width=400&height=300&seq=mlindustry001&orientation=landscape"
                  alt="Industry Solutions"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Industry Solutions</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Specialized machine learning solutions designed for specific industries and use cases.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Healthcare & life sciences</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Financial services</span>
                  </li>
                  <li className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-blue-600 mt-0.5 mr-2"></i>
                    <span>Manufacturing & logistics</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our ML Implementation Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a structured, collaborative approach to deliver machine learning solutions that address your specific business needs and integrate seamlessly with your existing systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Assessment */}
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-search-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Assessment</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We analyze your business needs, data readiness, and technical environment to determine the optimal ML approach.
                </p>
              </div>
            </div>

            {/* Planning */}
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Planning</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We develop a detailed project plan, including data requirements, model architecture, and implementation strategy.
                </p>
              </div>
            </div>

            {/* Development */}
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-code-s-slash-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Development</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our data scientists and ML engineers build, train, and refine your custom machine learning models.
                </p>
              </div>
            </div>

            {/* Integration */}
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">4</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-links-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Integration</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We seamlessly integrate ML models into your existing systems and workflows for immediate business impact.
                </p>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">5</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-customer-service-2-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Support</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We provide ongoing monitoring, maintenance, and optimization to ensure your ML solutions continue to deliver value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases & Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Use Cases & Benefits</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how machine learning transforms business operations and drives measurable results
            </p>
          </div>

          {/* Predictive Analytics */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Predictive Analytics</h3>
              <p className="text-gray-600 mb-8">
                Leverage advanced algorithms to forecast trends and make data-driven decisions with confidence.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Demand Forecasting</h4>
                    <p className="text-gray-600">Optimize inventory and resource allocation with accurate predictions.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Churn Prediction</h4>
                    <p className="text-gray-600">Identify at-risk customers and take proactive retention measures.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Maintenance Forecasting</h4>
                    <p className="text-gray-600">Predict equipment failures before they occur to minimize downtime.</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-line-chart-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">Success Metric</h4>
                </div>
                <p className="text-gray-700">
                  Our predictive analytics solutions have helped clients achieve up to <strong>35% improvement</strong> in forecast accuracy and <strong>25% reduction</strong> in operational costs.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/09d1c1e71407854304f79dc160d579f9.png"
                  alt="Predictive Analytics Dashboard"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>

          {/* Intelligent Automation */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 lg:order-1">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/d8b688e94f9038c0f0b975c6d75d37ee.png"
                  alt="Intelligent Automation Dashboard"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Intelligent Automation</h3>
              <p className="text-gray-600 mb-8">
                Streamline operations and reduce manual effort with AI-powered automation solutions.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Document Processing</h4>
                    <p className="text-gray-600">Automatically extract, classify, and process information from documents.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Quality Control</h4>
                    <p className="text-gray-600">Detect defects and anomalies with greater accuracy than manual inspection.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Customer Service Automation</h4>
                    <p className="text-gray-600">Provide intelligent routing and automated responses to common inquiries.</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-robot-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">Success Metric</h4>
                </div>
                <p className="text-gray-700">
                  Automation implementations have delivered <strong>80% reduction</strong> in processing times and <strong>60% cost savings</strong> in operational expenses.
                </p>
              </div>
            </div>
          </div>

          {/* Personalization & Recommendation */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Personalization & Recommendation</h3>
              <p className="text-gray-600 mb-8">
                Deliver personalized experiences and targeted recommendations to customers based on their preferences, behavior, and context.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Product Recommendations</h4>
                    <p className="text-gray-600">Increase cross-sell and upsell opportunities with personalized suggestions.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Content Personalization</h4>
                    <p className="text-gray-600">Deliver the most relevant content to each user based on their interests.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Dynamic Pricing</h4>
                    <p className="text-gray-600">Optimize pricing strategies based on demand, competition, and customer value.</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-user-star-line text-2xl text-blue-600"></i>
                  </div>
                  <h4 className="font-semibold text-gray-900">Success Metric</h4>
                </div>
                <p className="text-gray-700">
                  Our personalization solutions have helped clients achieve up to <strong>40% increase</strong> in conversion rates and <strong>30% improvement</strong> in customer engagement.
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/a0294a4f408e78f7124d47b52201e3e2.png"
                  alt="Personalization & Recommendation Dashboard"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technical Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive machine learning infrastructure powered by cutting-edge technologies and frameworks
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* ML Technologies & Frameworks */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="ri-code-box-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">ML Technologies & Frameworks</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Deep Learning Frameworks</p>
                    <p className="text-gray-600 text-sm">TensorFlow, PyTorch, Keras</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Classical ML Libraries</p>
                    <p className="text-gray-600 text-sm">Scikit-learn, XGBoost, LightGBM</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Cloud ML Platforms</p>
                    <p className="text-gray-600 text-sm">AWS SageMaker, Azure ML, Google AI Platform</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">MLOps Tools</p>
                    <p className="text-gray-600 text-sm">MLflow, Kubeflow, Apache Airflow</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ML Techniques */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-brain-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">ML Techniques</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Deep Learning</p>
                    <p className="text-gray-600 text-sm">Neural networks, CNNs, RNNs, Transformers</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Natural Language Processing</p>
                    <p className="text-gray-600 text-sm">Text analysis, sentiment analysis, language models</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Computer Vision</p>
                    <p className="text-gray-600 text-sm">Image classification, object detection, segmentation</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Reinforcement Learning</p>
                    <p className="text-gray-600 text-sm">Decision optimization, autonomous systems</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Integration Capabilities */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <i className="ri-links-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Integration Capabilities</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Enterprise Systems</p>
                    <p className="text-gray-600 text-sm">ERP, CRM, BI Tools integration</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">API Development</p>
                    <p className="text-gray-600 text-sm">RESTful APIs, GraphQL, WebSockets</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">Deployment Options</p>
                    <p className="text-gray-600 text-sm">Cloud-based, On-premises, Hybrid, Edge computing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-8 shadow-lg text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold">Performance Metrics</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-blue-100 font-medium">Model Accuracy</span>
                    <span className="text-white font-bold">95%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white rounded-full h-3" style={{width: '95%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-blue-100 font-medium">Processing Speed</span>
                    <span className="text-white font-bold">92%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white rounded-full h-3" style={{width: '92%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-blue-100 font-medium">Scalability</span>
                    <span className="text-white font-bold">98%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white rounded-full h-3" style={{width: '98%'}}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-blue-100 font-medium">Reliability</span>
                    <span className="text-white font-bold">99%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white rounded-full h-3" style={{width: '99%'}}></div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/20">
                <p className="text-blue-100 text-sm">
                  Our ML solutions consistently deliver enterprise-grade performance with 99.9% uptime and sub-second response times.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}